import { useCallback, useEffect, useState, FC } from 'react';
import { useAppDispatch, useAppSelector } from 'app/store';
import { getSearchProfilesValidations } from 'app/store/Profile/selectors';
import { fetchSearchProfilesValidationsEffect } from 'app/store/Profile/effects';
import {
  fetchContributionClassesRolesDDLEffect,
  fetchCreateContributionValidationsEffect,
  fetchPatchContributionValidationEffect,
} from 'app/store/SongList/effects';
import { getUserCountrySelector } from 'app/store/Countries/selectors';
import {
  Contribution,
  ContributionClassName,
  ContributionPatchValidationSchema,
} from 'types/contribution';
import {
  getAffectsSplit,
  getContributionClassesAndRoles,
  getCreateContributionValidation,
} from 'app/store/SongList/selectors';
import { useResource } from 'app/utils/hooks';
import { DDL } from 'types/general.types';
import { ContributionFormLoader } from './ContributionFormLoader';
import { ContributionFormComponent } from './ContributionFormComponent';
import { useContributionState } from './hooks';

interface ContributionFormProps {
  contribution?: Contribution;
  onClose: () => void;
  countries: DDL;
}

export const ContributionForm: FC<ContributionFormProps> = ({
  contribution: contributionProp,
  onClose,
  countries,
}) => {
  const [patchValidations, setPatchValidations] = useState<{
    [className: string]: ContributionPatchValidationSchema;
  }>({});
  const [
    newContributionClass,
    setNewContributionClass,
  ] = useState<ContributionClassName>();
  const {
    contribution,
    createOrUpdateContribution,
    handleContributionDelete,
  } = useContributionState({
    contribution: contributionProp,
    newContributionClass,
  });
  const dispatch = useAppDispatch();
  const userCountry = useAppSelector(getUserCountrySelector);
  const createValidation = useAppSelector(getCreateContributionValidation);
  const searchProfilesValidation = useAppSelector(getSearchProfilesValidations);
  const [contributionClassesAndRoles] = useResource(
    fetchContributionClassesRolesDDLEffect,
    getContributionClassesAndRoles
  );

  const affectsSplit = useAppSelector(
    getAffectsSplit(contribution?.className || newContributionClass)
  );
  const isLoading = !(
    contributionClassesAndRoles &&
    createValidation &&
    searchProfilesValidation
  );

  useEffect(() => {
    if (!createValidation) {
      dispatch(fetchCreateContributionValidationsEffect());
    }
    if (!searchProfilesValidation) {
      dispatch(fetchSearchProfilesValidationsEffect());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const fetchPatchValidation = useCallback(
    async (className: ContributionClassName) => {
      try {
        const validation = await dispatch(
          fetchPatchContributionValidationEffect({ className })
        ).unwrap();
        if (newContributionClass && !patchValidations[newContributionClass]) {
          setPatchValidations({
            ...patchValidations,
            [newContributionClass]: validation,
          });
        }
      } catch {
        // proceed
      }
    },
    [dispatch, newContributionClass, patchValidations]
  );

  useEffect(() => {
    if (newContributionClass && !patchValidations[newContributionClass]) {
      fetchPatchValidation(newContributionClass);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchPatchValidation, newContributionClass]);

  if (isLoading) {
    return <ContributionFormLoader full={Boolean(contributionProp)} />;
  }

  return (
    <ContributionFormComponent
      contribution={contributionProp}
      contributionClassesAndRoles={contributionClassesAndRoles}
      createValidation={createValidation}
      countries={countries}
      userCountry={userCountry}
      createOrUpdateContribution={createOrUpdateContribution}
      handleContributionDelete={handleContributionDelete}
      affectsSplit={affectsSplit}
      setNewContributionClass={setNewContributionClass}
      searchProfilesValidation={searchProfilesValidation}
      patchValidation={patchValidations[newContributionClass || '']}
      onClose={onClose}
    />
  );
};
